// ./src/styles/theme/colors.ts
import { MantineColorsTuple } from "@mantine/core";

// ============================================
// FormSubmits Brand Colors
// Aligned with landing website design system
// ============================================

// Brand - Deep Indigo/Violet
// Conveys: Security, trust, sophistication, technical excellence
export const brand: MantineColorsTuple = [
  "#f3f1ff", // 0 - Lightest backgrounds, subtle highlights
  "#e4e0ff", // 1 - Very light hover states
  "#cdc6ff", // 2 - Light secondary backgrounds
  "#a99aff", // 3 - Medium light borders, dividers
  "#8b78ff", // 4 - Medium secondary buttons
  "#6c5ce7", // 5 - Primary brand color - main CTAs
  "#5a4bd1", // 6 - Darker hover on primary
  "#4839a8", // 7 - Dark active states
  "#372c82", // 8 - Very dark text on light
  "#271f5c", // 9 - Darkest headings
];

// Accent - Electric Cyan
// For highlights, badges, special interactive elements
export const accent: MantineColorsTuple = [
  "#e6fcff", // 0
  "#c4f5fc", // 1
  "#8eedf7", // 2
  "#4de1f0", // 3
  "#00d4e8", // 4 - Main accent
  "#00b8d4", // 5
  "#009db8", // 6
  "#007a91", // 7
  "#005a6b", // 8
  "#003d49", // 9
];

// Success - Emerald Green
// Validation passed, clean data accepted, positive states
export const success: MantineColorsTuple = [
  "#ecfdf5", // 0
  "#d1fae5", // 1
  "#a7f3d0", // 2
  "#6ee7b7", // 3
  "#34d399", // 4
  "#10b981", // 5 - Main success
  "#059669", // 6
  "#047857", // 7
  "#065f46", // 8
  "#064e3b", // 9
];

// Error - Coral Red
// Validation failed, data blocked, negative states
export const error: MantineColorsTuple = [
  "#fef2f2", // 0
  "#fee2e2", // 1
  "#fecaca", // 2
  "#fca5a5", // 3
  "#f87171", // 4
  "#ef4444", // 5 - Main error
  "#dc2626", // 6
  "#b91c1c", // 7
  "#991b1b", // 8
  "#7f1d1d", // 9
];

// Warning - Amber
// Caution states, attention needed, pending actions
export const warning: MantineColorsTuple = [
  "#fffbeb", // 0
  "#fef3c7", // 1
  "#fde68a", // 2
  "#fcd34d", // 3
  "#fbbf24", // 4
  "#f59e0b", // 5 - Main warning
  "#d97706", // 6
  "#b45309", // 7
  "#92400e", // 8
  "#78350f", // 9
];

// Neutral - Slate Grays
// Professional, technical feel with slight blue undertone
export const neutral: MantineColorsTuple = [
  "#f8fafc", // 0 - Off-white backgrounds
  "#f1f5f9", // 1 - Light gray backgrounds
  "#e2e8f0", // 2 - Borders, dividers
  "#cbd5e1", // 3 - Disabled states
  "#94a3b8", // 4 - Placeholder text
  "#64748b", // 5 - Secondary text
  "#475569", // 6 - Body text
  "#334155", // 7 - Dark text
  "#1e293b", // 8 - Headings
  "#0f172a", // 9 - Near black
];

// ============================================
// Semantic Color Aliases
// Use these for consistent meaning across the app
// ============================================
export const semanticColors = {
  // Form validation states
  validationPassed: success[5],
  validationFailed: error[5],
  validationWarning: warning[5],

  // Form status
  formActive: success[5],
  formDisabled: neutral[4],
  formScheduled: accent[4],
  formExpired: neutral[5],

  // Submission status
  submissionSuccess: success[5],
  submissionBlocked: error[5],
  submissionPending: warning[5],

  // Security indicators
  securityStrong: success[5],
  securityWeak: error[5],
  securityModerate: warning[5],

  // General UI
  info: accent[5],
  link: brand[5],
  linkHover: brand[6],
} as const;

// ============================================
// Background Colors
// ============================================
export const backgrounds = {
  primary: "#ffffff",
  secondary: neutral[0],
  tertiary: neutral[1],
  brand: brand[0],
  accent: accent[0],
} as const;

// ============================================
// Text Colors
// ============================================
export const textColors = {
  primary: neutral[8],
  secondary: neutral[5],
  dimmed: neutral[4],
  inverse: "#ffffff",
  brand: brand[5],
  accent: accent[5],
} as const;

// ============================================
// Border Colors
// ============================================
export const borderColors = {
  light: neutral[2],
  medium: neutral[3],
  dark: neutral[4],
  brand: brand[3],
} as const;

// ============================================
// Gradients
// For hero sections, CTAs, special elements
// ============================================
export const gradients = {
  primary: "linear-gradient(135deg, #6c5ce7 0%, #8b78ff 100%)",
  hero: "linear-gradient(135deg, #271f5c 0%, #6c5ce7 50%, #00d4e8 100%)",
  subtle: "linear-gradient(180deg, #f3f1ff 0%, #ffffff 100%)",
  dark: "linear-gradient(135deg, #0f172a 0%, #271f5c 100%)",
} as const;

// ============================================
// Color Palette Export
// ============================================
export const colorPalette = {
  brand,
  accent,
  success,
  error,
  warning,
  neutral,
  semantic: semanticColors,
  backgrounds,
  text: textColors,
  borders: borderColors,
  gradients,
} as const;
